import { ActionIcon, Badge, Box, Button, Grid, Group, Loader, Modal, Paper, Skeleton, Table, Tabs, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { notifications, showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { FaEllipsisV, FaFileExport, FaListAlt, FaPlay, FaTag } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import AudioPlayer from '../components/audio_player';
import SimpleHeader from '../components/simple_header';
import { EXPORT_CALLS, GET_ALL_CALLS, GET_CALL_DETAILS, GET_CALLS_DYNAMIC_FILTERS, RUN_AFTER_CALL } from '../services/calls';
import { formatMilliseconds, getExtenseDatetime, groupBy, toTitleCase, useQuery } from '../utility/util';
import InputField, { InputFieldTypes } from '../components/input_field';
import { GET_ALL_CAMPAIGNS } from '../services/campaigns';
import ConversationBox from '../components/conversations/conversation_box';
import moment from 'moment';
import { Buffer } from 'buffer';
import { useAuth } from '../contexts/auth.context';
import { useLanguage } from '../contexts/language.context';
import { FaFilter } from 'react-icons/fa6';
import { GET_ALL_AGENTS } from '../services/agents';
import { useRealtimeCalls } from '../services/rt/calls';
import InfiniteScroll from 'react-infinite-scroll-component';
import RichAudioPlayer from '../components/rich_audio_player';

export default function CallsPage() {
    const { calls: rtCalls } = useRealtimeCalls();
    const [calls, setCalls] = useState<any>({});
    const [page, setPage] = useState(0);
    const [selectedTab, setSelectedTab] = useState("general");
    const [filters, setFilters] = useState<any>({
        status: [],
        direction: null,
        campaign: null,
        period: [],
    });
    const [openFilters, setOpenFilters] = useState<any>(null);
    const [done, setDone] = useState(false);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [loadingDynamicFilters, setLoadingDynamicFilters] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [dynamicFilters, setDynamicFilters] = useState<any[]>([]);
    const [loadingCallDetails, setLoadingCallDetails] = useState<boolean>(false);
    const [expandedCall, setExpandedCall] = useState<any>(null);
    const [isExpandedCall, setIsExpandedCall] = useState<any>(null);
    const [extractedVariables, setExtractedVariables] = useState<any[]>([]);
    // const [quantity, setQuantity] = useState(10);

    const navigate = useNavigate();
    const search = useQuery();
    const theme = useMantineTheme();
    const { userData } = useAuth();
    const { str } = useLanguage();
    const campaignQuery = search.get("campaign");

    const key = `${filters.campaign};${filters.status.join("---")};${filters.direction};${filters.period.join("---")};;DF;;${dynamicFilters.filter(df => ({ ...filters?.extracted })[df.key] !== undefined).map(df => `${df.key}:${{ ...filters?.extracted }[df.key]}`).join("---")}`;

    const loadCalls = () => {
        GET_ALL_CALLS({ ...filters, page })
            .then(({ items, total }) => {
                setLoaded(true);
                setDone(items.length === 0);
                setTotal(total);
                setCalls(cs => ({
                    ...cs,
                    [key]: groupBy([...(cs[key] ?? []), ...items], c => c.id)
                        .map(g => g[1][0])
                        .sort((a, b) => a.createdAt > b.createdAt ? -1 : 1)
                }));
            })
            .catch(err => {
                showNotification({ title: "Ops", message: err.message, color: "red" })
            })
    }

    const loadCampaigns = () => {
        setLoadingCampaigns(true);
        GET_ALL_CAMPAIGNS()
            .then(res => {
                setLoadingCampaigns(false);
                setCampaigns(res.filter(c => c.status !== -1));
            })
            .catch(err => {
                setLoadingCampaigns(false);
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const loadDynamicFilters = () => {
        setLoadingDynamicFilters(true);
        GET_CALLS_DYNAMIC_FILTERS()
            .then(res => {
                setLoadingDynamicFilters(false);
                setDynamicFilters(res);
            })
            .catch(err => {
                setLoadingDynamicFilters(false);
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const handleExportCsv = () => {
        setLoadingExport(true)
        EXPORT_CALLS({ ...filters, page })
            .then(res => {
                setLoadingExport(false)
                for (let file of res.files ?? []) {
                    if (file.url) window.open(file.url, "_blank");
                    if (file.base64) {
                        const byteCharacters = Buffer.from(file.base64, "base64");
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters[i];
                        }
                        const byteArray = new Uint8Array(byteNumbers);

                        const blob = new Blob([byteArray], { type: file.contentType });

                        const url = URL.createObjectURL(blob);;
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${file.filename}`;
                        a.click();
                        URL.revokeObjectURL(url);
                    }
                }
            })
            .catch(err => {
                setLoadingExport(false)
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const afterCall = (callId) => {
        RUN_AFTER_CALL(callId)
            .then(res => {
                notifications.show({ message: JSON.stringify(res) });
            })
            .catch(err => {
                notifications.show({ message: err.message, color: 'red' })
            });
    }

    useEffect(() => {
        const ev = [];
        for (let key in { ...expandedCall?.extracted_variables }) {
            ev.push({
                key,
                value: expandedCall?.extracted_variables[key],
            })
        }
        setExtractedVariables(ev);
    }, [expandedCall]);

    useEffect(() => {
        loadCampaigns();
    }, []);

    useEffect(() => {
        loadDynamicFilters();
    }, []);

    useEffect(() => {
        setFilters(f => ({ ...f, campaign: campaignQuery }));
    }, [campaignQuery]);

    useEffect(() => {
        loadCalls();
    }, [key, page])

    useEffect(() => {
        if (page === 0) setLoaded(false);
    }, [key, page])

    useEffect(() => {
        if (isExpandedCall) {
            setLoadingCallDetails(true);
            GET_CALL_DETAILS(isExpandedCall?.id)
                .then(setExpandedCall)
                .catch(showNotification)
                .finally(() => { setLoadingCallDetails(false) });
        } else {
            setExpandedCall(null);
        }
    }, [isExpandedCall])

    return <Box>
        <Paper mb="xl" p="md">
            <Grid align="flex-end">
                <Grid.Col span={{ base: 12, md: 2 }} ta="right">
                    <Tooltip label="Export data">
                        <Button fullWidth size="md" loading={loadingExport} onClick={() => {
                            handleExportCsv()
                        }} rightSection={<FaFileExport />}>{str("EXPORT")}</Button>
                    </Tooltip>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 3 }}>
                    <InputField
                        name="campaign"
                        loading={loadingCampaigns}
                        placeholder={str("SEARCH")}
                        title={str("CAMPAIGNS.TITLE")}
                        fieldType={InputFieldTypes.SELECT}
                        value={filters?.campaign}
                        onChange={({ campaign }) => {
                            setPage(0);
                            setFilters(f => ({ ...f, campaign }));
                        }}
                        options={campaigns.map((c) => ({ label: `${c.title}`, value: c.id }))}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <InputField
                        name="period"
                        title={str("PERIOD")}
                        fieldType={InputFieldTypes.DATERANGE}
                        value={filters?.period.map(p => p ? moment(p).toDate() : p)}
                        onChange={({ period }) => {
                            setPage(0);
                            setFilters(f => ({
                                ...f, period: [
                                    period[0] ? moment(period[0]).startOf("day").format() : null,
                                    period[1] ? moment(period[1]).endOf("day").format() : null,
                                ]
                            }));
                        }}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                    <InputField
                        name="status"
                        title={str("STATUS")}
                        fieldType={InputFieldTypes.SELECT}
                        value={filters?.status}
                        multiple
                        onChange={({ status }) => { setPage(0); setFilters(f => ({ ...f, status })); }}
                        options={[
                            { label: str(`CALLS.STATUS.NO-ANSWER`), value: 'no-answer' },
                            { label: str(`CALLS.STATUS.PICKUP`), value: 'pickup' },
                            { label: str(`CALLS.STATUS.OUTCOME`), value: 'outcome' },
                            { label: str(`CALLS.STATUS.BUSY`), value: 'busy' },
                            { label: str(`CALLS.STATUS.VOICEMAIL`), value: 'voicemail' },
                            { label: str(`CALLS.STATUS.FAILED`), value: 'failed' },
                        ]}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 1 }}>
                    <ActionIcon
                        size="xl"
                        color="dark"
                        onClick={() => { setOpenFilters(filters) }}
                    ><FaFilter /></ActionIcon>
                </Grid.Col>
            </Grid>
        </Paper>

        {/* {shownCalls.length === 0 && <div style={{ textAlign: 'center', padding: 40 }}>
            <UnstyledButton onClick={() => setNewContact({})}>
                <Paper p="lg" shadow="xs">
                    <Group>
                        <ActionIcon><FaEdit /></ActionIcon>
                        <Text c="gray">Create new call</Text>
                    </Group>
                    {searchContact.length > 0 && <Text ta="center" c="violet">{searchContact}</Text>}
                </Paper>
            </UnstyledButton>
        </div>} */}
        <Text size="lg" c="gray.1">{str("TOTAL_RESULTS")}: {total}</Text>
        <InfiniteScroll
            dataLength={(calls[key] ?? []).length}
            next={() => setPage(p => p + 1)}
            hasMore={!loaded || (calls[key] ?? []).length < total}
            loader={Array.from(Array(10)).map(it => <Skeleton ta="center" mb="sm" p="xl"></Skeleton>)}
            endMessage={done && (calls[key] ?? []).length > 0 ? <Text c="gray" size="md" mt="xl" ta="center">{str("CALLS.NO_MORE")}...</Text> : null}
        >
            <Table.ScrollContainer minWidth={100}>
                <Table mt="xl" styles={{ tr: {}, th: { fontSize: 16 } }}>
                    <Table.Tr>
                        {userData?.user?.recordingsEnabled && <Table.Th></Table.Th>}
                        <Table.Th>{str("CALLS.CONTACT")}</Table.Th>
                        <Table.Th>{str("CALLS.FROM")}</Table.Th>
                        <Table.Th>{str("CALLS.TO")}</Table.Th>
                        <Table.Th>{str("CALLS.DIRECTION")}</Table.Th>
                        <Table.Th>{str("CALLS.ORIGIN")}</Table.Th>
                        <Table.Th>{str("CALLS.COST_DURATION")}</Table.Th>
                        <Table.Th>{str("CALLS.DATE")}</Table.Th>
                        <Table.Th>{str("STATUS")}</Table.Th>
                        <Table.Th></Table.Th>
                    </Table.Tr>
                    {(calls[key] ?? []).map(c =>
                        <Table.Tr key={c.id}>
                            {userData?.user?.recordingsEnabled && <Table.Td>
                                <AudioPlayer url={c?.recording_url}>
                                    <ActionIcon disabled={!c?.recording_url} variant="outline" color="gray" size="lg"><FaPlay /></ActionIcon>
                                </AudioPlayer>
                            </Table.Td>}
                            <Table.Td><Link to={`/contacts/${c?.contact?.id}`}><Text size="sm" c="gray.0">{c?.contact?.name}</Text></Link></Table.Td>
                            <Table.Td ta="center">{c.from ?? "web"}</Table.Td>
                            <Table.Td ta="center">{c.to ?? "web"}</Table.Td>
                            <Table.Td ta="center">{c.direction === "inbound" ? str("CALLS.INBOUND") : str("CALLS.OUTBOUND")}</Table.Td>
                            <Table.Td ta="center">{c.campaign ? c.campaign.title : str("CALLS.ROLEPLAY")} - {c?.agent?.title}</Table.Td>
                            {
                                userData?.plan?.plan_type === "packages"
                                    ? <Table.Td ta="center">{formatMilliseconds((c?.duration ?? 0) * 1000)}</Table.Td>
                                    : <Table.Td ta="center">${(c?.total_price).toFixed(2)} / {formatMilliseconds((c?.duration ?? 0) * 1000)}</Table.Td>
                            }
                            <Table.Td ta="center">{getExtenseDatetime(c?.createdAt)}</Table.Td>
                            <Table.Td ta="center"><Badge color={{
                                "failed": "red",
                                "pickup": "blue",
                                "completed": "blue",
                                "outcome": "green",
                                "voicemail": "violet",
                                "busy": "yellow",
                                "no-answer": "yellow",
                            }[(c.call_status ?? c.status) as string] ?? "gray"} variant="filled">{str(`CALLS.STATUS.${(c.call_status ?? c.status ?? "").toUpperCase()}`)}</Badge></Table.Td>
                            <Table.Td>
                                <Group>
                                    <ActionIcon color="dark" onClick={() => setIsExpandedCall(c)}><FaListAlt /></ActionIcon>
                                    {
                                        userData?.user?.isAdmin &&
                                        <ActionIcon color="dark" onClick={() => afterCall(c.id)}><FaTag /></ActionIcon>
                                    }
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table>
            </Table.ScrollContainer>
        </InfiniteScroll>

        <Modal
            opened={!!isExpandedCall}
            onClose={() => setIsExpandedCall(false)}
            size="lg"
        >
            {
                <Tabs value={selectedTab ?? "general"} onChange={setSelectedTab}>
                    <Tabs.List>
                        <Tabs.Tab value="general">{str("CALLS.GENERAL")}</Tabs.Tab>
                        {userData?.user?.recordingsEnabled && <Tabs.Tab value="transcription" disabled={!expandedCall?.transcription}>{str("CALLS.TRANSCRIPTION")}</Tabs.Tab>}
                        {userData?.user?.recordingsEnabled && <Tabs.Tab value="analysis" disabled={!expandedCall?.analysis && extractedVariables.length === 0}>{str("CALLS.ANALYSIS")}</Tabs.Tab>}
                        {loadingCallDetails && <Tabs.Tab value="loading"><Loader size="sm" color={theme.colors.gray[2]} /></Tabs.Tab>}

                    </Tabs.List>
                    <Tabs.Panel value="general" pt="md" pb="md">
                        <Badge mt="md" color={{
                            "failed": "red",
                            "pickup": "blue",
                            "completed": "blue",
                            "outcome": "green",
                            "voicemail": "violet",
                            "busy": "yellow",
                            "no-answer": "yellow",
                        }[expandedCall?.status] ?? "gray"} variant="outline">{str(`CALLS.STATUS.${(expandedCall?.status ?? "").toUpperCase()}`)}</Badge>

                        <Text mt="sm">{str("CALLS.CONTACT")}: {expandedCall?.contact?.name}</Text>
                        <Text mt="sm">{str("CALLS.FROM")}: {expandedCall?.from ?? "web"}</Text>
                        <Text mt="sm">{str("CALLS.TO")}: {expandedCall?.to ?? "web"}</Text>

                        <Text mt="sm">{str("CALLS.DIRECTION")}: {expandedCall?.direction === "inbound" ? str("CALLS.INBOUND") : str("CALLS.OUTBOUND")}</Text>
                        <Text mt="sm">{str("CALLS.ORIGIN")}: {expandedCall?.campaign ? expandedCall?.campaign.title : str("CALLS.ROLEPLAY")}</Text>
                        {
                            userData?.plan?.plan_type === "packages"
                                ? <Text mt="sm">{str("CALLS.COST_DURATION")}: {formatMilliseconds((expandedCall?.duration ?? 0) * 1000)}</Text>
                                : <Text mt="sm">{str("CALLS.COST_DURATION")}: ${(expandedCall?.total_price ?? 0).toFixed(2)} / {formatMilliseconds((expandedCall?.duration ?? 0) * 1000)}</Text>
                        }
                        <Text mt="sm">{str("CALLS.DATE")}: {getExtenseDatetime(expandedCall?.createdAt)}</Text>

                        {expandedCall?.recording_url && userData?.user?.recordingsEnabled && (
                            <RichAudioPlayer src={expandedCall?.recording_url} title="Recording" />
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel value="transcription">
                        <ConversationBox
                            messages={(expandedCall?.transcription ?? [])}
                            reverse
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="analysis">
                        {expandedCall?.analysis && <>
                            {expandedCall?.analysis?.call_completion_rating && <Group mt="md">
                                <Text style={{ flex: 1 }}>{str("CALLS.COMPLETION")}</Text>
                                <Badge variant="outline" color={{
                                    "Incomplete": "red",
                                    "Positive": "green",
                                    "Complete": "green",
                                    "Neutral": "yellow",
                                }[expandedCall?.analysis?.call_completion_rating as string] ?? "gray"}>{expandedCall?.analysis?.call_completion_rating}</Badge>
                            </Group>}
                            {expandedCall?.analysis?.agent_task_completion_rating && <>
                                <Text size="md" c="gray">{expandedCall?.analysis?.call_completion_rating_reason}</Text>
                                <Group mt="md">
                                    <Text style={{ flex: 1 }}>{str("CALLS.AGENT_TASK_COMPLETION")}</Text>
                                    <Badge variant="outline" color={{
                                        "Incomplete": "red",
                                        "Positive": "green",
                                        "Complete": "green",
                                        "Neutral": "yellow",
                                    }[expandedCall?.analysis?.agent_task_completion_rating as string] ?? "gray"}>{str(`CALLS.ANALYSIS_STATUS.${(expandedCall?.analysis?.agent_task_completion_rating ?? "").toUpperCase()}`)}</Badge>
                                </Group>
                            </>}
                            <Text size="md" c="gray">{expandedCall?.analysis?.agent_task_completion_rating_reason}</Text>
                            <Group mt="md">
                                <Text style={{ flex: 1 }}>{str("CALLS.USER_SENTIMENT")}:</Text><Badge variant="outline">{expandedCall?.analysis?.user_sentiment}</Badge>
                            </Group>

                        </>}
                        {extractedVariables.length > 0 && <>
                            {extractedVariables.filter(v => v.key !== "awaz_outcome").map(v => <Box pt="md">
                                <Group>
                                    <Text style={{ flex: 1 }} size="md">{v.key === "awaz_outcome_reason" ? "Outcome Reason" : toTitleCase(v.key)}</Text>
                                    {v.key === "awaz_outcome_reason" && extractedVariables.find(v => v.key === "awaz_outcome")?.value === true && <Badge color='green'>OUTCOME</Badge>}
                                </Group>
                                <Text size="md" c="gray">{[false, true].includes(v.value) ? (v.value ? "Yes" : "No") : JSON.stringify(v.value)}</Text>
                            </Box>)}
                        </>}
                    </Tabs.Panel>
                </Tabs>
            }
        </Modal>

        <Modal
            opened={!!openFilters}
            onClose={() => setOpenFilters(null)}
        >
            <Group style={{ flexDirection: 'column', alignItems: 'unset', justifyContent: 'unset' }}>
                <InputField
                    name="campaign"
                    loading={loadingCampaigns}
                    placeholder={str("SEARCH")}
                    title={str("CAMPAIGNS.TITLE")}
                    fieldType={InputFieldTypes.SELECT}
                    value={openFilters?.campaign}
                    onChange={({ campaign }) => {
                        setOpenFilters(f => ({ ...f, campaign }));
                    }}
                    options={campaigns.map((c) => ({ label: `${c.title}`, value: c.id }))}
                />

                <InputField
                    name="period"
                    title={str("PERIOD")}
                    fieldType={InputFieldTypes.DATERANGE}
                    value={openFilters?.period.map(p => p ? moment(p).toDate() : p)}
                    onChange={({ period }) => {
                        setOpenFilters(f => ({
                            ...f, period: [
                                period[0] ? moment(period[0]).startOf("day").format() : null,
                                period[1] ? moment(period[1]).endOf("day").format() : null,
                            ]
                        }));
                    }}
                />

                <InputField
                    name="status"
                    title={str("STATUS")}
                    fieldType={InputFieldTypes.SELECT}
                    value={openFilters?.status}
                    multiple
                    onChange={({ status }) => { setOpenFilters(f => ({ ...f, status })); }}
                    options={[
                        { label: str(`CALLS.STATUS.NO-ANSWER`), value: 'no-answer' },
                        { label: str(`CALLS.STATUS.PICKUP`), value: 'pickup' },
                        { label: str(`CALLS.STATUS.OUTCOME`), value: 'outcome' },
                        { label: str(`CALLS.STATUS.BUSY`), value: 'busy' },
                        { label: str(`CALLS.STATUS.VOICEMAIL`), value: 'voicemail' },
                        { label: str(`CALLS.STATUS.FAILED`), value: 'failed' },
                    ]}
                />

                {
                    loadingDynamicFilters
                        ? <Loader />
                        : dynamicFilters.map(df => (
                            <InputField
                                name="vl"
                                title={df.key}
                                fieldType={InputFieldTypes.SELECT}
                                value={{ ...openFilters?.extracted }[df.key] !== undefined ? `${{ ...openFilters?.extracted }[df.key]}` : { ...openFilters?.extracted }[df.key]}
                                onChange={({ vl }) => { setOpenFilters(f => ({ ...f, extracted: { ...f.extracted, [df.key]: vl === "true" ? true : vl === "false" ? false : undefined } })); }}
                                options={[
                                    { label: str(`YES`), value: 'true' },
                                    { label: str(`NO`), value: 'false' },
                                ]}
                            />
                        ))
                }

                <Group justify='flex-end'>
                    <Button variant="light" onClick={() => setOpenFilters(null)}>{str("CANCEL")}</Button>
                    <Button onClick={() => {
                        setPage(0);
                        setFilters(openFilters);
                        setOpenFilters(null);
                    }}>{str("SAVE")}</Button>
                </Group>
            </Group>
        </Modal>
    </Box>
}